import { Form } from 'formik'
import React from 'react'
import { Box, Divider, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import Select from 'src/Libs/inputs/formik/Select'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { NumberField } from 'src/Libs/inputs/formik'
import { makeStyles } from 'tss-react/mui'
import { useLabeledStorageOptions } from '../../hooks'
import { StorageInstallationStatementKeys } from '../../models/storage'
import { UnitTypes } from 'src/Libs/types'
import { EdisonWallBoxToggle } from 'src/Libs/installation-statement/components/EdisonWallbox'

const useStyles = makeStyles()(theme => ({
  divider: {
    borderColor: theme.palette.shades.shade03,
  },
  box: {
    [theme.breakpoints.up('md')]: {
      flex: 1,
    },
  },
}))

/**
 * Component to display the storage step form
 *
 */
const StorageForm: React.VFC = () => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const {
    ConnectionTypeOptions,
    WeatherConditionOptions,
    NetConfigurationOptions,
    InstallationTypeOptions,
    ApplicationAreaOptions,
    WallMountedOptions,
    EmergencyOptions,
  } = useLabeledStorageOptions()

  return (
    <Form
    // placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
    >
      <Stack spacing={4} direction={{ xs: 'column', md: 'row' }}>
        <Box className={classes.box}>
          <Typography sx={{ pb: 2 }} variant="h5">
            {t('installation-statement.storage.form.general.title')}
          </Typography>
          <Stack spacing={4}>
            <Select
              label={t('installation-statement.storage.form.general.connection-type')}
              required
              name={StorageInstallationStatementKeys.ConnectionType}
              options={ConnectionTypeOptions}
            />
            <Select
              label={t('installation-statement.storage.form.general.weather-conditions')}
              required
              name={StorageInstallationStatementKeys.WeatherConditions}
              options={WeatherConditionOptions}
            />
            <NumberField
              label={t('installation-statement.storage.form.general.temperature')}
              required
              name={StorageInstallationStatementKeys.Temperature}
              fullWidth
              suffix={UnitTypes.celsius}
              isDecimal
              precision={1}
              ignoreDefaultValue
            />
            <Select
              label={t('installation-statement.storage.form.general.net-configuration')}
              required
              name={StorageInstallationStatementKeys.NetConfiguration}
              options={NetConfigurationOptions}
            />
          </Stack>

          <Typography variant="h5" sx={{ pt: 4, pb: 2 }}>
            {t('installation-statement.storage.form.setup.title')}
          </Typography>
          <Stack spacing={4}>
            <Select
              label={t('installation-statement.storage.form.setup.wall-mounted')}
              required
              name={StorageInstallationStatementKeys.WallMounted}
              options={WallMountedOptions}
            />
            <Select
              label={t('installation-statement.storage.form.setup.installation-type')}
              required
              name={StorageInstallationStatementKeys.InstallationType}
              options={InstallationTypeOptions}
            />
          </Stack>
        </Box>
        {isDesktop && <Divider orientation="vertical" flexItem className={classes.divider} />}
        <Box className={classes.box}>
          <Stack spacing={4}>
            <Select
              label={t('installation-statement.storage.form.setup.application-area')}
              required
              name={StorageInstallationStatementKeys.ApplicationArea}
              options={ApplicationAreaOptions}
            />
            <Select
              label={t('installation-statement.storage.form.setup.emergency-option')}
              required
              name={StorageInstallationStatementKeys.EmergencyOption}
              options={EmergencyOptions}
            />
          </Stack>

          <Typography variant="h5" sx={{ pt: 4, pb: 2 }}>
            {t('installation-statement.storage.form.distances.title')}
          </Typography>

          <Grid container rowSpacing={4} columnSpacing={2}>
            <Grid item xs={12} md={6}>
              <NumberField
                name={StorageInstallationStatementKeys.DistanceTop}
                label={t('installation-statement.storage.form.distances.top')}
                required
                fullWidth
                suffix={UnitTypes.centiMeter}
                isDecimal
                precision={2}
                ignoreDefaultValue
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberField
                name={StorageInstallationStatementKeys.DistanceFront}
                label={t('installation-statement.storage.form.distances.front')}
                required
                fullWidth
                suffix={UnitTypes.centiMeter}
                isDecimal
                precision={2}
                ignoreDefaultValue
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberField
                name={StorageInstallationStatementKeys.DistanceLeft}
                label={t('installation-statement.storage.form.distances.left')}
                required
                fullWidth
                suffix={UnitTypes.centiMeter}
                isDecimal
                precision={2}
                ignoreDefaultValue
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberField
                name={StorageInstallationStatementKeys.DistanceRight}
                label={t('installation-statement.storage.form.distances.right')}
                required
                fullWidth
                suffix={UnitTypes.centiMeter}
                isDecimal
                precision={2}
                ignoreDefaultValue
              />
            </Grid>
            <EdisonWallBoxToggle />
          </Grid>
        </Box>
      </Stack>
    </Form>
  )
}

export default StorageForm
